<!-- Poster mode: split-screen with image on the left -->
@if (showSignupPoster) {
  <div class="login-split-layout">
    <div class="poster-section" style="background-image: url('assets/creatives/login-image.jpg')"></div>

    <div class="form-section">
      <div class="card pb-6 pt-8 sm:pt-10 px-6 sm:px-10 max-w-[440px] w-full">
        <ng-container *ngTemplateOutlet="signupCard" />
      </div>
    </div>
  </div>
} @else {
  <!-- Default mode: centered card -->
  <div class="flex items-center justify-center p-4 sm:p-8 w-full min-h-screen">
    <div class="card pb-6 pt-8 sm:pt-10 px-6 sm:px-10 max-w-[440px] w-full">
      <ng-container *ngTemplateOutlet="signupCard" />
    </div>
  </div>
}

<!-- =========== SHARED SIGNUP CARD CONTENT =========== -->
<ng-template #signupCard>
  <div class="flex justify-center">
    <img class="signin-logo" src="assets/creatives/logo-w70.png" alt="SkillzTank logo" />
  </div>

  @if (inviteInfo()) {
    <p class="text-xl font-bold text-[#1D2433] mb-1 text-center">Créer un compte pour rejoindre {{ inviteInfo().workspaceName }}</p>
    <p class="text-sm text-[#5B6372] mb-6 text-center">Vous avez été invité par {{ inviteInfo().inviterName }}. Créez votre compte pour accepter.</p>
  } @else {
    <p class="text-xl font-bold text-[#1D2433] mb-1 text-center">{{ 'Sessions.Signup.Title' | translate }}</p>
    <p class="text-sm text-[#5B6372] mb-6 text-center">{{ 'Sessions.Signup.Subtitle' | translate }}</p>
  }

  <!-- Error message -->
  @if (errorMessage()) {
    <div class="flex items-center gap-2 bg-red-50 text-red-700 rounded-lg px-3 py-2 mb-4 text-sm animate-fade-in-up">
      <span>{{ errorMessage() }}</span>
    </div>
  }

  <!-- Provider buttons (primary CTAs) -->
  <div class="flex flex-col gap-2 mb-3">
    <button matButton="tonal" class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]" [disabled]="loading()" (click)="signupWithProvider('google')">
      <img src="assets/images/google.svg" class="h-4 mr-2" />
      <span>{{ 'Sessions.Signup.ContinueWithGoogle' | translate }}</span>
    </button>

    <button
      matButton="tonal"
      class="provider-login w-full border border-gray-200 rounded-lg justify-center flex items-center h-[42px]"
      [disabled]="loading()"
      (click)="signupWithProvider('microsoft')"
    >
      <img src="assets/images/microsoft.svg" class="h-4 mr-2" />
      <span>{{ 'Sessions.Signup.ContinueWithMicrosoft' | translate }}</span>
    </button>
  </div>

  <!-- Or divider -->
  <div class="or-divider flex items-center gap-3 my-3">
    <div class="flex-1 h-px bg-gray-200"></div>
    <span class="text-[13px] text-gray-400 whitespace-nowrap">{{ 'Sessions.Signup.OrSignUpWithEmail' | translate }}</span>
    <div class="flex-1 h-px bg-gray-200"></div>
  </div>

  <!-- Email form (always visible) -->
  <form [formGroup]="signupForm" class="signup-form" (ngSubmit)="signupWithEmail()">
    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Signin.FullNameField' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input id="signup-full-name" matInput formControlName="fullName" type="text" autocomplete="name" [placeholder]="'Sessions.Signin.FullNamePlaceholder' | translate" />
      </mat-form-field>
    </div>

    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Signup.EmailField' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input id="signup-email" matInput formControlName="email" type="email" autocomplete="email" [placeholder]="'Sessions.Signup.EmailPlaceholder' | translate" />
      </mat-form-field>
    </div>

    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Signup.PasswordField' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input
          id="signup-password"
          matInput
          formControlName="password"
          [type]="hidePassword() ? 'password' : 'text'"
          autocomplete="new-password"
          [placeholder]="'Sessions.Signup.PasswordPlaceholder' | translate"
        />
        <button mat-icon-button matSuffix type="button" (click)="togglePasswordVisibility()" [attr.aria-label]="'Toggle password'">
          <mat-icon>{{ hidePassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
      </mat-form-field>
    </div>

    <div class="w-full mb-2">
      <mat-label class="form-label mb-1">{{ 'Sessions.Login.ConfirmPasswordField' | translate }}</mat-label>
      <mat-form-field subscriptSizing="dynamic" appearance="fill" class="soft-input w-full">
        <input
          id="signup-confirm-password"
          matInput
          formControlName="confirmPassword"
          [type]="hideConfirmPassword() ? 'password' : 'text'"
          autocomplete="new-password"
          [placeholder]="'Sessions.Login.ConfirmPasswordPlaceholder' | translate"
        />
        <button mat-icon-button matSuffix type="button" (click)="toggleConfirmPasswordVisibility()" [attr.aria-label]="'Toggle password'">
          <mat-icon>{{ hideConfirmPassword() ? 'visibility_off' : 'visibility' }}</mat-icon>
        </button>
      </mat-form-field>
      <p class="text-xs text-gray-400 mt-1">{{ 'Sessions.Signup.PasswordHint' | translate }}</p>
      @if (passwordMismatch) {
        <p class="text-xs text-red-500 mt-1 ml-1">{{ 'Sessions.Login.PasswordMismatch' | translate }}</p>
      }
    </div>

    <!-- Honeypot field to prevent bot spam -->
    <div class="absolute opacity-0 -z-10 w-0 h-0 pointer-events-none" aria-hidden="true">
      <mat-label class="form-label mb-1">Website URL</mat-label>
      <mat-form-field appearance="fill" class="w-full">
        <input matInput formControlName="website" type="text" autocomplete="off" tabindex="-1" placeholder="Enter your website URL" />
      </mat-form-field>
    </div>

    <button class="w-full py-1 text-sm rounded-lg font-medium" type="submit" [disabled]="loading() || !isFormValid" mat-flat-button color="primary">
      {{ 'Sessions.Signup.CreateAccount' | translate }}
    </button>
  </form>

  <!-- Loading bar -->
  <div class="mt-4">
    @if (loading()) {
      <mat-progress-bar mode="indeterminate" class="rounded-full"></mat-progress-bar>
      <p class="text-xs text-center text-gray-400 mt-2">{{ 'Sessions.Signup.CreatingWorkspace' | translate }}</p>
    }
  </div>

  <!-- Sign in link -->
  <div class="flex justify-center mt-3">
    <span class="text-sm text-gray-500">{{ 'Sessions.Signup.AlreadyHaveAccount' | translate }}&nbsp;</span>
    <a routerLink="/login" queryParamsHandling="merge" class="text-sm font-semibold text-primary hover:text-primary-dark cursor-pointer transition-colors">
      {{ 'Sessions.Signup.SignIn' | translate }}
    </a>
  </div>

  <app-language-selector class="mt-6 flex justify-center w-full" />

  <!-- Trust footer -->
  <div class="flex items-center justify-center gap-1.5 mt-4 text-gray-400 text-xs">
    <mat-icon class="text-[14px]! h-3.5! w-3.5!">lock</mat-icon>
    <span>{{ 'Sessions.Login.SecureConnection' | translate }}</span>
  </div>
</ng-template>
